import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class StatisticsExtractor {

  public count(
    values: any[] | undefined,
  ): number | undefined {

    if (values === null || values === undefined) {
      return undefined;
    }

    return values.length;
  }

  public aggregateCountsByGroup(
    values:
      {
        [group: string]: any[],
      }
      |
      {
        [outerGroup: string]: {
          [innerGroup: string]: any[],
        },
      }
      |
      undefined,
  ): { group: string; count: number }[] {

    if (!values) {
      return [];
    }

    if (this.is2DMapOfArrays(values)) {
      return this.aggregateCountsByGroup2D(values);
    }

    return Object.entries(values)
      .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
      .map(([group, values]) => ({
        group,
        count: this.uniqueByValue(values).length,
      }));
  }

  public aggregateNumericStatsByGroup(
    values: {
      [outerGroup: string]: {
        [innerGroup: string]: number,
      },
    } | undefined,
  ): {
    group: string;
    count: number;
    sum: number;
    min: number;
    max: number;
    avg: number;
  }[] {

    if (!values) {
      return [];
    }

    return Object.entries(values)
      .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
      .map(([group, innerGroup]) => {

        const numbers = Object.values(innerGroup);

        const count = numbers.length;
        const sum = numbers.reduce((sum, d) => sum + d, 0);
        const min = count > 0 ? Math.min(...numbers) : 0;
        const max = count > 0 ? Math.max(...numbers) : 0;
        const avg = count > 0 ? Math.round(sum / count) : 0;

        return {
          group,
          count,
          sum,
          min,
          max,
          avg,
        };
      });
  }

  public aggregateTotal(
    values: {
      [outerGroup: string]: {
        [innerGroup: string]: number,
      },
    } | undefined,
  ): number | undefined {

    if (!values) {
      return undefined;
    }

    let sum = 0;

    Object.values(values)
      .forEach((innerGroup) => {

        Object.values(innerGroup)
          .forEach((value: number) => sum += value);
      });

    return sum;
  }

  public aggregateUniqueCount(
    values:
      any[]
      |
      {
        [outerGroup: string]: any[],
      }
      |
      {
        [outerGroup: string]: {
          [innerGroup: string]: any[],
        },
      }
      |
      undefined,
  ): number | undefined {

    if (values === undefined) {
      return undefined;
    }

    if (Array.isArray(values)) {
      return this.uniqueByValue(values).length;
    }

    if (this.is2DMapOfArrays(values)) {
      return this.aggregateUniqueCount2D(values);
    }

    const allValues = Object.values(values).flat();

    return this.uniqueByValue(allValues).length;
  }

  public delta(
    values1: any[] | undefined,
    values2: any[] | undefined,
  ): number | undefined {

    if ((values1 === null || values1 === undefined)
      && (values2 === null || values2 === undefined)) {
      return undefined;
    }

    return (values1?.length || 0) - (values2?.length || 0);
  }

  public deltaSymbol(
    values1: any[] | undefined,
    values2: any[] | undefined,
  ): string {

    if ((values1 === null || values1 === undefined)
      && (values2 === null || values2 === undefined)) {
      return '';
    }

    const length1 = (values1?.length || 0);
    const length2 = (values2?.length || 0);

    if (length1 > length2) {
      return '+';
    }

    if (length1 < length2) {
      return '-';
    }

    return '';
  }

  private aggregateCountsByGroup2D(
    values: {
      [outerGroup: string]: {
        [innerGroup: string]: any[],
      },
    } | undefined,
  ): { group: string; count: number }[] {

    if (!values) {
      return [];
    }

    return Object.entries(values)
      .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
      .map(([group, innerGroups]) => {

        const allValues = Object.values(innerGroups).flat();

        return {
          group,
          count: this.uniqueByValue(allValues).length,
        };
      });
  }

  private aggregateUniqueCount2D(
    values: {
      [outerGroup: string]: {
        [innerGroup: string]: any[],
      },
    } | undefined,
  ): number | undefined {

    if (!values) {
      return undefined;
    }

    const allValues =
      Object.values(values)
        .flatMap(innerGroups => Object.values(innerGroups))
        .flat();

    return this.uniqueByValue(allValues).length;
  }

  private is2DMapOfArrays(
    obj: any,
  ): obj is { a: { b: any[] } } {

    if (typeof obj !== 'object' || obj === null) {
      return false;
    }

    for (const key1 in obj) {

      if (typeof obj[key1] !== 'object' || obj[key1] === null) {
        return false;
      }

      for (const key2 in obj[key1]) {

        if (!Array.isArray(obj[key1][key2])) {
          return false;
        }
      }
    }

    return true;
  }

  private uniqueByValue<T>(
    values: T[],
  ): T[] {

    const uniqueMap = new Map(
      values.map(value => [JSON.stringify(value), value]),
    );

    return Array.from(uniqueMap.values());
  }

}
